/**
 * Form Transformers
 *
 * Transform between form data and API format
 * Reuses transformation logic from V2 where applicable
 */

import { JobPostFormData } from "../schemas/jobPostValidation";
import { JobDescriptionEntity } from "../../../Entities/JobDescription";
import { JobPostMode } from "../types";
import {
  transformJobPostToStoreData,
  mapJobDescriptionToStep1,
  transformStep1ToFormValues,
  mapJobDescriptionToStep2,
  mapJobDescriptionToStep3,
  mapJobDescriptionToStep5,
} from "./v2Compatibility";
import { mapJobDescriptionToStep4 } from "../Steps/Step4HRScreening/utils/formTransformers";
import { InterviewRound, InterviewRoundType } from '../../../Dto/GenerateJobDescription.dto';

/**
 * Get initial form values based on mode
 */
export const getInitialFormValues = (
  mode: JobPostMode,
  jobPostId?: string
): JobPostFormData => {
  return {
    step0: {
      prompt: "",
    },
    step1: {
      title: "",
      position: "",
      numberOfOpenings: 1,
      noticePeriod: "0",
      ctc: null,
      currency: undefined,
      currencyCode: "INR",
      endDate: null,
      description: "",
      notifications: { email: true, whatsapp: true },
    },
    step2: {
      requirements: [],
      additionalRequirements: [],
      history: [], // Initialize empty history array
    },
    step3: {
      interviewRounds: [],
      isProfileMatchRequired: true,
      interviewRoundExpiry: 5,
    },
    step4: {
      manualScreeningQuestions: [],
      // jobAlignment is optional - no default initialization
    },
    step5: {
      onlineInterview: false,
      interviewers: [],
      interviewGuests: [],
    },
    step6: {},
    currentStep: 0,
    mode,
    jobPostId,
  };
};

/**
 * Transform JobDescriptionEntity to form data
 * Handles both new format (ctc as number + currency as separate field) and old format (ctc as "VALUE CURRENCY" string)
 */
export const transformJobPostToFormData = (
  jobPost: JobDescriptionEntity,
  mode: JobPostMode
): JobPostFormData => {
  // Reuse V2 transformer logic
  const stepData = transformJobPostToStoreData(jobPost);

  // Extract CTC and currency - prioritize new format (separate fields), fallback to old format
  let ctcValue: number | null = null;
  let currencyCode = "INR";

  // Check if backend has separate currency field (new format)
  const backendCurrency = (jobPost as any).currency;
  if (backendCurrency) {
    currencyCode = backendCurrency;
  }

  // Handle CTC - new format: number, old format: string "VALUE CURRENCY"
  if (jobPost.ctc !== null && jobPost.ctc !== undefined) {
    try {
      if (typeof jobPost.ctc === "number") {
        // New format: CTC is already a number
        if (!isNaN(jobPost.ctc) && jobPost.ctc >= 0) {
          ctcValue = jobPost.ctc;
          // Use backend currency if available, otherwise default to INR
          currencyCode = backendCurrency || "INR";
        } else {
          console.warn("Invalid CTC number value:", jobPost.ctc);
        }
      } else if (typeof jobPost.ctc === "string") {
        // Old format: Parse "260000 INR" string
        const parts = jobPost.ctc.trim().split(/\s+/);
        if (parts.length >= 2) {
          const numericPart = parts[0];
          const currencyPart = parts.slice(1).join(" ").toUpperCase();
          const numericValue = Number(numericPart);
          if (!isNaN(numericValue) && numericValue >= 0) {
            ctcValue = numericValue;
            currencyCode = currencyPart || backendCurrency || "INR";
          } else {
            console.warn("Invalid numeric part in CTC string:", numericPart);
          }
        } else {
          // Try to parse as pure number string
          const numericValue = Number(jobPost.ctc);
          if (!isNaN(numericValue) && numericValue >= 0) {
            ctcValue = numericValue;
            currencyCode = backendCurrency || "INR";
          } else {
            console.warn("Invalid CTC string format:", jobPost.ctc);
          }
        }
      } else {
        console.warn("Unexpected CTC type:", typeof jobPost.ctc, jobPost.ctc);
      }
    } catch (error) {
      console.error(
        "Error parsing CTC value:",
        error,
        "CTC value:",
        jobPost.ctc
      );
      // Fallback: try to extract number if possible
      if (typeof jobPost.ctc === "string") {
        const numericValue = Number(jobPost.ctc);
        if (!isNaN(numericValue) && numericValue >= 0) {
          ctcValue = numericValue;
          currencyCode = backendCurrency || "INR";
        }
      }
    }
  }

  // Get form values for step1 (reuse V2 transformer for other fields)
  const step1FormValues = transformStep1ToFormValues(
    stepData.step1,
    currencyCode
  );

  return {
    step0: {
      prompt: "",
      // Include skills extracted from job description if available
      skills: Array.isArray(jobPost.skills) 
        ? jobPost.skills.filter((s: any) => typeof s === 'string' && s.trim().length > 0)
        : [],
    },
    step1: {
      title: jobPost.title || "",
      position: jobPost.position || "",
      numberOfOpenings: jobPost.numberOfOpenings || 1,
      noticePeriod: String(jobPost.noticePeriod || "0"),
      ctc: ctcValue ?? step1FormValues.ctc ?? null,
      currency: currencyCode || undefined,
      currencyCode: currencyCode || "INR",
      endDate: step1FormValues.endDate || jobPost.endDate || null,
      description: jobPost.description || "",
      notifications: {
        email: mode === "new" ? true : jobPost.notifications?.email === true,
        whatsapp: mode === "new" ? true : jobPost.notifications?.whatsapp === true,
      },
    },
    step2: {
      requirements: stepData.step2?.requirements || [],
      additionalRequirements: stepData.step2?.additionalRequirements || [],
    },
    step3: {
      // Initialize _displayMetadata for rounds in edit mode to ensure proper validation
      // This prevents validation errors when form is first loaded before metadata enrichment
      // Also filter out any potential duplicates and ensure Profile Matching is handled correctly
      interviewRounds: (() => {
        const rounds = stepData.step3?.interviewRounds || [];
        const seenIds = new Set<string>();
        const processedRounds: any[] = [];
        
        // Normalize round ID for comparison (handle Profile Matching and null cases)
        const getRoundId = (round: any): string | null => {
          const id = round.interviewRoundId || (round as any)._displayMetadata?._id;
          if (!id) return null;
          // Normalize Profile Matching IDs
          const idStr = String(id).toLowerCase().trim();
          if (idStr === 'profile-match' || idStr === 'profilematching') {
            return 'profile-match';
          }
          return id;
        };
        
        for (const round of rounds) {
          const roundId = getRoundId(round);
          
          // Skip if we've already seen this ID (prevent duplicates)
          if (roundId && seenIds.has(roundId)) {
            console.warn('[transformJobPostToFormData] Skipping duplicate round:', roundId, round);
            continue;
          }
          
          if (roundId) {
            seenIds.add(roundId);
          }
          
          // If round already has _displayMetadata, keep it but ensure _id is set
          if ((round as any)._displayMetadata) {
            const existingMetadata = (round as any)._displayMetadata;
            // Ensure _id is set in metadata if it's missing
            if (!existingMetadata._id && round.interviewRoundId) {
              processedRounds.push({
                ...round,
                _displayMetadata: {
                  ...existingMetadata,
                  _id: round.interviewRoundId,
                },
              });
            } else {
              processedRounds.push(round);
            }
          } else {
            // Otherwise, initialize _displayMetadata with _id set to interviewRoundId
            // This ensures transformStoreToStep3 can properly extract _id for validation
            processedRounds.push({
              ...round,
              _displayMetadata: {
                _id: round.interviewRoundId || undefined,
                // Other metadata fields will be populated by metadata enrichment
              },
            });
          }
        }
        
        return processedRounds;
      })(),
      isProfileMatchRequired: jobPost.isProfileMatchRequired ?? true,
      interviewRoundExpiry: jobPost.interviewRoundExpiry ?? 5,
    },
    step4: mapJobDescriptionToStep4(jobPost) || {
      manualScreeningQuestions: [],
      // jobAlignment is optional - no default initialization
    },
    step5: {
      onlineInterview: jobPost.onlineInterview ?? false,
      interviewers: jobPost.interviewers || [],
      interviewGuests: jobPost.interviewGuests || [],
    },
    step6: {},
    currentStep: 1,
    mode,
    jobPostId: jobPost._id,
  };
};

/**
 * Transform form data to API payload
 * Sends CTC as number and currency as separate string field
 */
export const transformFormDataToPayload = (
  formData: JobPostFormData,
  allRoundsInDB: InterviewRound[]
): Partial<JobDescriptionEntity> => {

  const jobAlignmentRoundInDB = allRoundsInDB.find(
    (round) => round.isGeneric && round.title.trim().replaceAll(/\s+/g, '').toLowerCase() === "jobalignment"
  );

  const overallAssessmentRoundInDB = allRoundsInDB.find(
    (round) => round.isGeneric && round.title.trim().replaceAll(/\s+/g, '').toLowerCase() === "overallassessment"
  );
  // Get CTC value (number only) and currency (separate field)
  let ctcValue: number | undefined = formData.step1?.ctc ?? undefined;
  let currencyValue: string | undefined =
    formData.step1?.currency || formData.step1?.currencyCode || undefined;

  // Validate and handle CTC
  if (ctcValue !== null && ctcValue !== undefined) {
    // Ensure CTC is a valid positive number
    if (typeof ctcValue !== "number" || isNaN(ctcValue) || ctcValue < 0) {
      console.warn(
        "Invalid CTC value provided:",
        ctcValue,
        "Defaulting to undefined"
      );
      ctcValue = undefined;
    }
  }

  // Default currency to INR if CTC is provided but currency is missing
  if (ctcValue !== null && ctcValue !== undefined && !currencyValue) {
    currencyValue = "INR";
    console.log("Currency not provided, defaulting to INR");
  }

  // Validate currency format (should be a non-empty string)
  if (
    currencyValue &&
    (typeof currencyValue !== "string" || !currencyValue.trim())
  ) {
    console.warn(
      "Invalid currency value provided:",
      currencyValue,
      "Defaulting to INR"
    );
    currencyValue = "INR";
  }

  // Get isAIGenerated status from jobAlignment
  const isAIGenerated = formData.step4?.jobAlignment?.isAIGenerated ?? true;

  // Filter out any existing manual_questions from Step2's additionalRequirements
  // (they should only come from Step4's manualScreeningQuestions to avoid duplication)
  const step2AdditionalRequirements = (
    formData.step2?.additionalRequirements || []
  ).filter((req) => req.topic !== "manual_questions");

  // Transform Step 4 manual screening questions to additionalRequirements format
  // Manual screening questions get topic "manual_questions"
  // Always include manual questions if they exist (independent of isAIGenerated)
  // Manual questions and AI questions are optional and independent - both can coexist
  const manualScreeningQuestions =
    (formData.step4?.manualScreeningQuestions || []).length > 0
      ? (formData.step4.manualScreeningQuestions || []).map(
          (question, index) => ({
            id: String(question.id || index + 1),
            topic: "manual_questions", // Mark as manual questions
            description: question.description || "",
          })
        )
      : [];

  // Merge Step 2 additionalRequirements (without manual_questions) with Step 4 manual screening questions
  const additionalRequirements = [
    ...step2AdditionalRequirements,
    ...manualScreeningQuestions,
  ];

  // Build payload matching existing API structure
  const payload: Partial<JobDescriptionEntity> = {
    title: formData.step1?.title || "",
    position: formData.step1?.position || "",
    numberOfOpenings: formData.step1?.numberOfOpenings || 1,
    noticePeriod: parseInt(formData.step1?.noticePeriod || "0", 10),
    description: formData.step1?.description || "",
    isProfileMatchRequired: formData.step3?.isProfileMatchRequired ?? true,
    // Ensure requirements have all required fields including id (assigned as index + 1), topic, description, and mustMatch
    requirements: (formData.step2?.requirements || []).map((req, index) => ({
      id: index + 1, // Always assign ID as index + 1 for consistency
      topic: req.topic || "", // Ensure topic is always a string (empty per FR-009)
      description: req.description || "",
      mustMatch: req.mustMatch ?? false,
    })),
    additionalRequirements: additionalRequirements, // Includes Step 2 additionalRequirements and Step 4 custom screening questions
    interviewRoundExpiry: formData.step3?.interviewRoundExpiry ?? 5,
    // Strip display metadata from interview rounds before sending to API
    // Include isAIGenerated (always true for Step 3 rounds)
    interviewRounds: (formData.step3?.interviewRounds || [])
      .filter((round: any) => round.interviewRoundId !== "profile-match")
      .map((round: any) => ({
        interviewRoundId:
          round.interviewRoundId === "job-alignment"
            ? jobAlignmentRoundInDB?._id || "job-alignment"
            : round.interviewRoundId === "overall-assessment"
            ? overallAssessmentRoundInDB?._id || "overall-assessment"
            : round.interviewRoundId,
        interviewRoundNumber: round.interviewRoundNumber - 1,
        isMustQualify: round.isMustQualify ?? true,
        passPercentage: round.passPercentage, // Include pass percentage if provided
        isAIGenerated: true,
      })),
    // Ensure interviewers have email field and correct referenceType
    interviewers: (formData.step5?.interviewers || []).map((interviewer) => ({
      ...interviewer,
      email: interviewer.email || "", // Ensure email is always a string
      referenceType: (interviewer.referenceType || "User") as "User", // Ensure referenceType is "User"
    })),
    interviewGuests: formData.step5?.interviewGuests || [],
    onlineInterview: formData.step5?.onlineInterview ?? false,
    notifications: {
      email: formData.step1?.notifications?.email === true,
      whatsapp: formData.step1?.notifications?.whatsapp === true,
    },
  };

  // Handle CTC - send as number (not string)
  if (ctcValue !== null && ctcValue !== undefined) {
    payload.ctc = ctcValue;
  }

  // Add currency as separate string field
  if (currencyValue) {
    (payload as any).currency = currencyValue;
  }

  // Handle endDate
  if (formData.step1?.endDate) {
    payload.endDate = formData.step1.endDate;
  }

  // Append Job Alignment round from Step 4 (last position) only if:
  // 1. isAIGenerated is true (AI questions will be generated), OR
  // 2. Manual questions exist (independent of isAIGenerated)
  // If neither exists, skip job-alignment (round is not shown to candidates)
  if (formData.step4?.jobAlignment) {
    const isAIGenerated = formData.step4.jobAlignment.isAIGenerated ?? true;
    const hasManualQuestions =
      (formData.step4?.manualScreeningQuestions || []).length > 0;

    // Job alignment round exists if:
    // - isAIGenerated is true (AI questions will be generated), OR
    // - Manual questions exist
    // If neither exists, round is not included (hidden from candidates)
    if (isAIGenerated || hasManualQuestions) {
      const step3Rounds = payload.interviewRounds || [];
      const maxRoundNumber =
        step3Rounds.length > 0
          ? Math.max(
              ...step3Rounds.map((r: any) => r.interviewRoundNumber || 0)
            )
          : 0;

      const jobAlignmentRound: InterviewRoundType = {
        interviewRoundId: jobAlignmentRoundInDB?._id || "job-alignment",
        interviewRoundNumber: maxRoundNumber + 1,
        isMustQualify: (formData.step4?.jobAlignment as any)?.isMustQualify ?? false,
        passPercentage: (formData.step4?.jobAlignment as any)?.passPercentage,
        isAIGenerated: isAIGenerated,
      };

      payload.interviewRounds = [...step3Rounds, jobAlignmentRound];
    }
  }

  // Include ATS integration fields if present (for syncing imported job posts)
  // These are stored in step0 and will be included in drafts automatically
  const step0 = formData.step0 as { 
    prompt?: string; 
    appJobId?: string; 
    appType?: string; 
    skills?: string[];
  } | undefined;
  if (step0?.appJobId) {
    payload.appJobId = step0.appJobId;
    console.log('[transformFormDataToPayload] Adding appJobId to payload:', step0.appJobId);
  }
  if (step0?.appType) {
    payload.appType = step0.appType;
    console.log('[transformFormDataToPayload] Adding appType to payload:', step0.appType);
  }

  // Include skills extracted from AI generation
  // Skills are stored in step0 after AI generation and persisted in drafts
  if (step0?.skills && step0.skills.length > 0) {
    payload.skills = step0.skills;
    console.log('[transformFormDataToPayload] Adding skills to payload:', step0.skills);
  }

  return payload;
};
